import { useBulkEditState, useParentItemsFromBulkEditSelection } from './bulk-edit.state';

import { CloseIconButton } from '../../../../shared/components/IconButton';
import { Item } from '../../types/item.types';
import Pill from '@ingka/pill';
import React from 'react';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useRemoveItemFromSelection } from '../../state/selection.state';

type EditPillProps = {
   item: Item;
   lastItem: boolean;
   disableClose?: boolean;
};

const StyledPillSpan = styled.span`
   display: flex;
   align-items: center;
`;

const StyledLabelDiv = styled.div`
   color: ${theme.colors.text.primary};
   padding-right: ${tokens.space.sm};
   display: block;
   text-align: left;
`;

const StyledPill = styled(Pill) <{ selected: boolean }>`
   background: ${({ selected }) => selected ? theme.colors.bg.tertiary : 'white'};
   cursor: default;
   white-space: nowrap;
   border: 1px solid ${theme.colors.border.default};
   padding-top: ${tokens.space.sm};
   padding-bottom: ${tokens.space.sm};
   margin-left: ${tokens.space.xs};
   margin-right: ${tokens.space.xs};

   :hover {
      background: ${({ selected }) => selected ? theme.colors.bg.tertiary : 'white'};
   }
`;

const StyledItemNoDiv = styled.div`
   font-weight: ${tokens.font.weight.normal};
`;

export const BulkEditPill = ({ item, lastItem, disableClose }: EditPillProps) => {
   const selectedParentItemNumbers = useParentItemsFromBulkEditSelection();
   const removeItemFromSelection = useRemoveItemFromSelection(item);

   const handleRemove = () => {
      const isInBulkEditSelection = selectedParentItemNumbers?.some(
         parentItem => parentItem?.itemNo?.toString() === item.itemNo.toString()
      );

      if (isInBulkEditSelection) {
         useBulkEditState.setState({ rowSelection: {}, selectedIds: new Set() });
      }

      removeItemFromSelection();
   };

   if (!item || !item.itemNo) {
      return null;
   }

   const itemNo = item.itemNo.toString();
   let itemNoString = itemNo;

   if (itemNo.length >= 6) {
      const part1 = itemNo.substring(0, 3);
      const part2 = itemNo.substring(3, 6);
      const part3 = itemNo.length > 6 ? itemNo.substring(6) : '';
      itemNoString = part3 ? `${part1}.${part2}.${part3}` : `${part1}.${part2}`;
   }

   const isSelected = !disableClose && selectedParentItemNumbers?.some(
      parentItem => parentItem?.itemNo?.toString() === itemNo
   );

   return (
      <StyledPill
         selected={isSelected}
         label={
            <StyledPillSpan>
               <StyledLabelDiv>
                  <StyledItemNoDiv>{itemNoString}</StyledItemNoDiv>
               </StyledLabelDiv>

               {!disableClose && (
                  <CloseIconButton
                     onClick={handleRemove}
                     disabled={lastItem}
                     title={lastItem ? 'Cannot remove last item' : 'Remove item from bulk edit'}
                  />
               )}
            </StyledPillSpan>
         }
         iconPosition={'trailing'}
         size={"small"}
      />
   );
};
